import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import config from '../config/config';
import { FaTimes, FaTrash, FaPlus, FaCheck, FaPencilAlt } from 'react-icons/fa';

// ── Endpoints reais do backend (ServicoProtocoloController), mapeados em
// "/servicoProtocolo". A empresa é inferida pelo backend a partir do JWT —
// não precisa (nem existe endpoint que aceite) um parâmetro empresaCodigo.
// IMPORTANTE: esse controller não tem endpoint de reordenação nem uma rota
// separada "/todos" — por isso a ordem de exibição aqui segue a ordem de
// cadastro, e a lista traz o que "listar()" (service.buscarPorEmpresa())
// devolver. Se um endpoint de reordenar for adicionado depois, é só plugar
// aqui de novo. ──
const ConfigServicosModal = ({ aberto, onFechar, onAtualizado, addToast }) => {
  const { API_BASE_URL } = config;
  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

  const [itens, setItens] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(false);

  const [editandoId, setEditandoId]     = useState(null);
  const [labelEdicao, setLabelEdicao]   = useState('');
  const [salvandoId, setSalvandoId]     = useState(null);

  const [novoLabel, setNovoLabel]       = useState('');
  const [novoAceitaTexto, setNovoAceitaTexto] = useState(false);
  const [adicionando, setAdicionando]   = useState(false);

  const carregarLista = useCallback(function() {
    setCarregandoLista(true);
    return axios.get(`${API_BASE_URL}/servicoProtocolo`, getAuthHeader())
      .then(function(res) {
        setItens(res.data || []);
      })
      .catch(function() {
        if (addToast) addToast('Erro ao carregar serviços', 'error');
      })
      .finally(function() { setCarregandoLista(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  useEffect(function() {
    if (aberto) carregarLista();
  }, [aberto, carregarLista]);

  if (!aberto) return null;

  function atualizarTudo() {
    carregarLista();
    if (onAtualizado) onAtualizado();
  }

  function iniciarEdicao(item) {
    setEditandoId(item.id);
    setLabelEdicao(item.nome);
  }

  function cancelarEdicao() {
    setEditandoId(null);
    setLabelEdicao('');
  }

  function salvarEdicao(item) {
    if (!labelEdicao.trim()) return;
    setSalvandoId(item.id);
    axios.put(`${API_BASE_URL}/servicoProtocolo/${item.id}`, {
      nome: labelEdicao.trim(),
      aceitaTexto: item.aceitaTexto
    }, getAuthHeader())
      .then(function() {
        setEditandoId(null);
        atualizarTudo();
      })
      .catch(function() {
        if (addToast) addToast('Erro ao editar serviço', 'error');
      })
      .finally(function() { setSalvandoId(null); });
  }

  function alternarAceitaTexto(item) {
    setSalvandoId(item.id);
    axios.put(`${API_BASE_URL}/servicoProtocolo/${item.id}`, {
      nome: item.nome,
      aceitaTexto: !item.aceitaTexto
    }, getAuthHeader())
      .then(function() { atualizarTudo(); })
      .catch(function() {
        if (addToast) addToast('Erro ao atualizar serviço', 'error');
      })
      .finally(function() { setSalvandoId(null); });
  }

  // DELETE aqui é remoção de verdade (o controller expõe um DELETE puro,
  // sem endpoint de reativação) — por isso a confirmação avisa que é definitivo.
  function remover(item) {
    if (!window.confirm('Remover "' + item.nome + '" definitivamente? Essa ação não pode ser desfeita.')) {
      return;
    }
    setSalvandoId(item.id);
    axios.delete(`${API_BASE_URL}/servicoProtocolo/${item.id}`, getAuthHeader())
      .then(function() { atualizarTudo(); })
      .catch(function() {
        if (addToast) addToast('Erro ao remover serviço', 'error');
      })
      .finally(function() { setSalvandoId(null); });
  }

  function adicionarNovo() {
    if (!novoLabel.trim()) return;
    setAdicionando(true);
    axios.post(`${API_BASE_URL}/servicoProtocolo`, {
      nome: novoLabel.trim(),
      aceitaTexto: novoAceitaTexto
    }, getAuthHeader())
      .then(function() {
        setNovoLabel('');
        setNovoAceitaTexto(false);
        atualizarTudo();
      })
      .catch(function() {
        if (addToast) addToast('Erro ao adicionar serviço', 'error');
      })
      .finally(function() { setAdicionando(false); });
  }

  return (
    <Overlay onClick={onFechar}>
      <Painel onClick={function(e) { e.stopPropagation(); }}>
        <Cabecalho>
          <TituloModal>Configurar Serviços</TituloModal>
          <BotaoFechar onClick={onFechar}><FaTimes /></BotaoFechar>
        </Cabecalho>

        <Lista>
          {carregandoLista && <CarregandoTexto>Carregando...</CarregandoTexto>}

          {!carregandoLista && itens.map(function(item) {
            const emEdicao = editandoId === item.id;
            const carregando = salvandoId === item.id;
            return (
              <LinhaItem key={item.id} desabilitado={carregando}>
                {emEdicao ? (
                  <LabelInput
                    autoFocus
                    value={labelEdicao}
                    onChange={function(e) { setLabelEdicao(e.target.value); }}
                    onKeyDown={function(e) { if (e.key === 'Enter') salvarEdicao(item); if (e.key === 'Escape') cancelarEdicao(); }}
                    maxLength={60}
                  />
                ) : (
                  <LabelTexto>{item.nome}</LabelTexto>
                )}

                <AceitaTextoToggle
                  onClick={function() { alternarAceitaTexto(item); }}
                  ativo={item.aceitaTexto}
                  title="Permitir campo de texto livre (tipo 'OUTROS')"
                >
                  TEXTO LIVRE
                </AceitaTextoToggle>

                {emEdicao ? (
                  <>
                    <AcaoBtn onClick={function() { salvarEdicao(item); }} title="Salvar" cor="#006d6d">
                      <FaCheck size={12} />
                    </AcaoBtn>
                    <AcaoBtn onClick={cancelarEdicao} title="Cancelar" cor="#888">
                      <FaTimes size={12} />
                    </AcaoBtn>
                  </>
                ) : (
                  <>
                    <AcaoBtn onClick={function() { iniciarEdicao(item); }} title="Editar" cor="#006d6d">
                      <FaPencilAlt size={12} />
                    </AcaoBtn>
                    <AcaoBtn onClick={function() { remover(item); }} title="Remover definitivamente" cor="#c0392b">
                      <FaTrash size={12} />
                    </AcaoBtn>
                  </>
                )}
              </LinhaItem>
            );
          })}

          {!carregandoLista && itens.length === 0 && (
            <CarregandoTexto>Nenhum serviço cadastrado ainda.</CarregandoTexto>
          )}
        </Lista>

        <NovoItemLinha>
          <NovoInput
            placeholder="Novo serviço..."
            value={novoLabel}
            onChange={function(e) { setNovoLabel(e.target.value); }}
            onKeyDown={function(e) { if (e.key === 'Enter') adicionarNovo(); }}
            maxLength={60}
          />
          <NovoCheckboxLabel>
            <input
              type="checkbox"
              checked={novoAceitaTexto}
              onChange={function(e) { setNovoAceitaTexto(e.target.checked); }}
            />
            Texto livre
          </NovoCheckboxLabel>
          <BotaoAdicionar onClick={adicionarNovo} disabled={adicionando || !novoLabel.trim()}>
            <FaPlus size={11} style={{ marginRight: 6 }} />
            ADICIONAR
          </BotaoAdicionar>
        </NovoItemLinha>

        <Rodape>
          <BotaoConcluir onClick={onFechar}>CONCLUÍDO</BotaoConcluir>
        </Rodape>
      </Painel>
    </Overlay>
  );
};

export default ConfigServicosModal;

// ─── Estilos ──────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(20, 30, 35, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Painel = styled.div`
  width: 640px;
  max-width: 92vw;
  max-height: 84vh;
  background: white;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.3);
  display: flex;
  flex-direction: column;
  font-family: 'Segoe UI', Arial, sans-serif;
  overflow: hidden;
`;

const Cabecalho = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #006d6d, #009999);
  color: white;
`;

const TituloModal = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
`;

const BotaoFechar = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1rem;
  padding: 4px;
  display: flex;
  &:hover { opacity: 0.8; }
`;

const Lista = styled.div`
  padding: 12px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CarregandoTexto = styled.p`
  text-align: center;
  color: #aaa;
  font-size: 0.85rem;
  padding: 16px 0;
  margin: 0;
`;

const LinhaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #e2e6e9;
  border-radius: 8px;
  background: #fafbfc;
  opacity: ${function(p) { return p.desabilitado ? 0.5 : 1; }};
  pointer-events: ${function(p) { return p.desabilitado ? 'none' : 'auto'; }};
`;

const LabelTexto = styled.div`
  flex: 1;
  font-size: 0.9rem;
  font-weight: 600;
  color: #222;
  text-transform: uppercase;
`;

const LabelInput = styled.input`
  flex: 1;
  font-size: 0.9rem;
  font-weight: 600;
  color: #222;
  text-transform: uppercase;
  border: 1.5px solid #006d6d;
  border-radius: 4px;
  padding: 4px 8px;
  outline: none;
`;

const AceitaTextoToggle = styled.button`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  padding: 4px 8px;
  border-radius: 12px;
  border: 1.5px solid ${function(p) { return p.ativo ? '#006d6d' : '#ccc'; }};
  background: ${function(p) { return p.ativo ? '#e0f5f5' : 'white'; }};
  color: ${function(p) { return p.ativo ? '#006d6d' : '#999'; }};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  flex-shrink: 0;
`;

const AcaoBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1.5px solid ${function(p) { return p.cor || '#555'; }};
  background: white;
  color: ${function(p) { return p.cor || '#555'; }};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover { background: ${function(p) { return p.cor || '#555'; }}; color: white; }
`;

const NovoItemLinha = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid #e2e6e9;
  background: #f5f7f8;
`;

const NovoInput = styled.input`
  flex: 1;
  font-size: 0.9rem;
  padding: 8px 10px;
  border: 1.5px solid #ccc;
  border-radius: 6px;
  outline: none;
  text-transform: uppercase;
  &:focus { border-color: #006d6d; }
`;

const NovoCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: #444;
  white-space: nowrap;
  cursor: pointer;
`;

const BotaoAdicionar = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 14px;
  background: ${function(p) { return p.disabled ? '#aaa' : '#006d6d'; }};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: ${function(p) { return p.disabled ? 'not-allowed' : 'pointer'; }};
  white-space: nowrap;
`;

const Rodape = styled.div`
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #e2e6e9;
`;

const BotaoConcluir = styled.button`
  padding: 9px 22px;
  background: #1a2e3b;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #10202a; }
`;
